import { useState, useEffect } from 'react';
import SearchBar from '../components/SearchBar';
import MovieList from '../components/MovieList';
import CategoryTabs from '../components/CategoryTabs';
import { movieAPI } from '../services/api';
import type { Movie } from '../stores/movieStore';

const HomePage = () => {
  const [popularMovies, setPopularMovies] = useState<Movie[]>([]);
  const [latestMovies, setLatestMovies] = useState<Movie[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('全部');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const categories = ['全部', '电影', '电视剧', '动漫', '综艺', '纪录片'];

  useEffect(() => {
    fetchMovies();
  }, []);

  const fetchMovies = async () => {
    setLoading(true);
    setError(null);
    try {
      const [popularRes, latestRes] = await Promise.allSettled([
        movieAPI.getPopular(),
        movieAPI.getLatest(),
      ]);

      if (popularRes.status === 'fulfilled') {
        setPopularMovies(popularRes.value.data?.movies || []);
      }
      if (latestRes.status === 'fulfilled') {
        setLatestMovies(latestRes.value.data?.movies || []);
      }
    } catch (err) {
      console.error('Failed to fetch movies:', err);
      setError('加载失败，请刷新重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/20 rounded-full blur-3xl" />
          <div className="absolute top-40 right-20 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-10 left-1/3 w-80 h-80 bg-pink-500/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <div className="text-center mb-12 animate-fade-in">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              探索无限
              <span className="bg-gradient-to-r from-blue-400 via-purple-500 to-pink-500 bg-clip-text text-transparent">
                影视世界
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10">
              聚合多个影视资源平台，为您提供一站式的影视搜索、观看和管理体验
            </p>
            <div className="animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <SearchBar size="lg" />
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <CategoryTabs
          categories={categories}
          activeCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        />
      </section>

      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="glass rounded-xl p-4 text-center">
            <p className="text-red-400">{error}</p>
            <button onClick={fetchMovies} className="mt-2 px-4 py-2 bg-blue-500/20 text-blue-400 rounded-lg hover:bg-blue-500/30 transition-colors">
              重新加载
            </button>
          </div>
        </div>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-1 h-8 bg-gradient-to-b from-blue-500 to-purple-600 rounded-full" />
            <h2 className="text-2xl font-bold text-white">热门推荐</h2>
          </div>
          <a
            href="/search?sort=popular"
            className="text-blue-400 hover:text-blue-300 text-sm flex items-center space-x-1 transition-colors"
          >
            <span>查看更多</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
        <MovieList movies={popularMovies} loading={loading} emptyMessage="暂无热门推荐" />
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-1 h-8 bg-gradient-to-b from-green-500 to-teal-600 rounded-full" />
            <h2 className="text-2xl font-bold text-white">最新上线</h2>
          </div>
          <a
            href="/search?sort=latest"
            className="text-blue-400 hover:text-blue-300 text-sm flex items-center space-x-1 transition-colors"
          >
            <span>查看更多</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        </div>
        <MovieList movies={latestMovies} loading={loading} emptyMessage="暂无最新上线" />
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h2 className="text-2xl font-bold text-white text-center mb-12">为什么选择我们</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            {
              icon: '🔍',
              title: '多源聚合搜索',
              description: '整合多个影视资源平台，一次搜索即可找到所有可用资源',
            },
            {
              icon: '🎬',
              title: '高清流畅播放',
              description: '支持1080P、4K等多种画质，多播放源保证流畅体验',
            },
            {
              icon: '💾',
              title: '个人收藏管理',
              description: '收藏喜爱的影视作品，记录观看历史，随时续播',
            },
          ].map((feature) => (
            <div
              key={feature.title}
              className="glass rounded-2xl p-6 text-center card-hover"
            >
              <div className="text-4xl mb-4">{feature.icon}</div>
              <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
              <p className="text-gray-400 text-sm">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default HomePage;
